"use client";

/**
 * Meta Pixel mount point, and marketing attribution capture.
 *
 * Injects the Pixel base script via next/script (afterInteractive) and fires
 * PageView on initial load + every client-side route change. Also captures
 * fbclid into the _fbc cookie if one isn't already set.
 *
 * ⚠️ `fbq.disablePushState = true` in the snippet below is load-bearing — see
 * the note there. Without it every client-side navigation reports TWO
 * PageViews.
 *
 * It additionally records the marketing **first touch** (lib/attribution.ts),
 * which signup writes onto the user document. That lives here because it is
 * the same job on the same trigger — read the URL on mount and on every route
 * change — and because attribution capture must NOT be gated on the Pixel: a
 * Google or email campaign has UTMs and no Pixel involvement, and the capture
 * still has to happen when NEXT_PUBLIC_META_PIXEL_ID is unset.
 *
 * Mounted exactly once from <ClientLayout>.
 */

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import Bugsnag from '../lib/bugsnag';
import {
  captureFbclidIfMissing,
  isPixelEnabled,
  trackEvent,
} from '../lib/meta-pixel';
import { captureAttribution } from '../lib/attribution';
import { logError } from '../lib/bugsnag';
import { metaPixelId } from '../lib/metaPixelId';

const PIXEL_ID = metaPixelId();

export default function MetaPixel() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Depend on a string, not the ReadonlyURLSearchParams object. Next memoizes
  // that object on its context value today, so identity is stable, but an
  // analytics side effect shouldn't rest on a framework internal — and React
  // compares deps with Object.is, so a future instance change would silently
  // start reporting navigations that never happened.
  const query = searchParams?.toString() ?? '';
  // Belt and braces for a StrictMode double-invoke or a Suspense remount.
  const lastUrlRef = useRef<string | null>(null);

  // Capture fbclid into a .gobo.show-scoped _fbc cookie so the value
  // persists across subdomains and survives further navigation.
  useEffect(() => {
    if (!isPixelEnabled()) return;
    captureFbclidIfMissing();
  }, []);

  // The marketing first touch. Deliberately NOT behind isPixelEnabled(): a
  // Google Ads or newsletter link carries UTMs and no Pixel, and the touch has
  // to be recorded anyway. Re-run on route change because a client-side
  // navigation can be the first URL to carry parameters; the capture is a
  // no-op once a touch exists.
  useEffect(() => {
    captureAttribution(undefined, (error, meta) =>
      logError(error, { context: 'MetaPixel:captureAttribution', ...meta })
    );
  }, [pathname, query]);

  // Fire PageView on initial mount and on every client-side route change.
  //
  // We own every PageView: the snippet omits the base code's
  // fbq('track','PageView') and disables Pixel's own SPA tracking, so this is
  // the only emitter. Firing here rather than relying on Pixel's automatic
  // path is deliberate — that path never fires on initial load, only on a
  // history change.
  useEffect(() => {
    if (!isPixelEnabled()) return;
    // Query included so query-only changes fire, de-duplicated so a repeat of
    // the same URL cannot.
    const url = query ? `${pathname}?${query}` : pathname ?? '/';
    if (lastUrlRef.current === url) return;
    lastUrlRef.current = url;
    trackEvent('PageView');
  }, [pathname, query]);

  if (!PIXEL_ID) return null;

  // The standard Pixel snippet, parameterized with our id. We inline it so
  // the queue/stub is in place before any other code calls fbq().
  //
  // ⚠️ Two deliberate departures from Meta's copy-paste base code. Don't
  // "restore" either one:
  //
  //  1. **No `fbq('track','PageView')`.** The effect above owns it, so SPA
  //     navigations are covered too.
  //  2. **`fbq.disablePushState = true`.** fbevents.js monkey-patches
  //     history.pushState/replaceState and listens to popstate, firing its own
  //     PageView whenever location.href changes — built with
  //     `allowDuplicatePageViews: true`, so Meta does NOT dedupe it against
  //     ours. The App Router navigates via pushState, so without this flag
  //     every client-side route change reported two PageViews, inflating the
  //     count and every rate derived from it. Disabling their path and keeping
  //     ours means one PageView per navigation, initial load included, which
  //     their path alone never covers.
  const snippet = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${PIXEL_ID}');
    fbq.disablePushState = true;
  `;

  return (
    <>
      <Script
        id="meta-pixel-base"
        strategy="afterInteractive"
        // PageView itself is fired from the effect above so SPA navigations
        // are covered. We don't include a static <noscript> img since the
        // app surface is authenticated and requires JS regardless.
        onError={(err) => {
          Bugsnag.notify(
            err instanceof Error ? err : new Error('Meta Pixel script load failed'),
            (event) => {
              event.addMetadata('meta_pixel', { phase: 'script-load' });
            }
          );
        }}
        dangerouslySetInnerHTML={{ __html: snippet }}
      />
    </>
  );
}
